import { useState, useRef, useEffect, ChangeEvent } from 'react';
import { AutocompleteChangeReason, AutocompleteInputChangeReason } from '@material-ui/lab/Autocomplete';
import { useIsMobile } from '../../../utils/useIsMobile';
import { AutocompleteOptionModel } from './Autocomplete.model';

interface UseAutocompleteProps {
  value?: AutocompleteOptionModel | null;
  onChange?: (newValue: AutocompleteOptionModel | null, event: ChangeEvent<{}>, reason: AutocompleteChangeReason) => void;
  onInputChange?: (event: ChangeEvent<{}>, newValue: string, reason: AutocompleteInputChangeReason) => void;
  groupBy?: (option: AutocompleteOptionModel) => string;
  getOptionLabel: (option: AutocompleteOptionModel) => string;
}

interface UseAutocompleteReturn {
  // State
  option: AutocompleteOptionModel | null;
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
  isMobileIconDisplay: boolean;

  // Refs
  hasUserChangedOption: React.MutableRefObject<boolean>;
  autocompleteRef: React.MutableRefObject<HTMLInputElement | null>;

  // Handlers
  handleChange: (event: ChangeEvent<{}>, newValue: AutocompleteOptionModel | null, reason: AutocompleteChangeReason) => void;
  handleInputChange: (event: ChangeEvent<{}>, newValue: string, reason: AutocompleteInputChangeReason) => void;

  // Helpers
  isMobile: boolean;
}

export const useAutocomplete = ({
  value,
  onChange,
  onInputChange,
  groupBy,
  getOptionLabel
}: UseAutocompleteProps): UseAutocompleteReturn => {
  const isMobile = useIsMobile();

  const [option, setOption] = useState<AutocompleteOptionModel | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isMobileIconDisplay = Boolean(option && isMobile && groupBy);

  const hasUserChangedOption = useRef(false);
  const autocompleteRef = useRef<HTMLInputElement | null>(null);

  const handleChange = (event: ChangeEvent<{}>, newValue: AutocompleteOptionModel | null, reason: AutocompleteChangeReason) => {
    hasUserChangedOption.current = true;
    setOption(newValue);

    onChange && onChange(newValue, event, reason);

    // Nie zamykaj modalu gdy użytkownik czyści input przyciskiem X
    if (reason !== 'clear') {
      setIsModalOpen(false);
    }
  };

  const handleInputChange = (event: ChangeEvent<{}>, newValue: string, reason: AutocompleteInputChangeReason) => {
    hasUserChangedOption.current = option ? newValue === getOptionLabel(option) : false;

    onInputChange && onInputChange(event, newValue, reason);
  };

  useEffect(() => {
    setOption(value ?? null);

    if (isMobile && isModalOpen && autocompleteRef.current) {
      autocompleteRef.current.focus();
    }
  }, [isModalOpen, isMobile, value]);


  return {
    option,
    isModalOpen,
    setIsModalOpen,
    isMobileIconDisplay,

    hasUserChangedOption,
    autocompleteRef,

    handleChange,
    handleInputChange,

    isMobile
  };
};
